// /ViewManager/FieldConfigPanel.js
//
// Shared popover panel anchored to the ⋯ chip button.
// All sections are fully wired — no stubs.
//
// Props:
//   col            {object}   the field object currently being edited
//   anchorEl       {Element}  DOM element the ⋯ button was clicked on
//   onClose        {fn}       called when popover should close
//   onFieldChange  {fn}       (fieldName, propKey, newValue) => void

import React, { useState, useEffect, useCallback } from "react";
import { Popover, Slider, Divider, TextField } from "@mui/material";

import PushPinOutlinedIcon     from "@mui/icons-material/PushPinOutlined";
import PushPinIcon             from "@mui/icons-material/PushPin";
import SwapVertIcon            from "@mui/icons-material/SwapVert";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import ChatBubbleOutlineIcon   from "@mui/icons-material/ChatBubbleOutline";
import TuneIcon                from "@mui/icons-material/Tune";
import WidthNormalIcon         from "@mui/icons-material/WidthNormal";

import "./FieldConfigPanel.css";

// ── Constants ────────────────────────────────────────────────────────────────

const SORT_DIRECTIONS = [
  { value: "asc",  label: "Asc ↑" },
  { value: "desc", label: "Desc ↓" },
];

const SORT_ORDERS = [1, 2, 3, 4];

// ── Section wrapper ──────────────────────────────────────────────────────────
function Section({ icon, label, children }) {
  return (
    <div className="fop-section">
      <div className="fop-section-header">
        {icon && <span className="fop-section-icon">{icon}</span>}
        <span className="fop-section-label">{label}</span>
      </div>
      <div className="fop-section-body">{children}</div>
    </div>
  );
}

// ── Pill button ──────────────────────────────────────────────────────────────
function Pill({ label, active = false, danger = false, onClick }) {
  return (
    <button
      type="button"
      className={[
        "fop-pill",
        active ? "fop-pill--active" : "",
        danger ? "fop-pill--danger" : "",
      ].filter(Boolean).join(" ")}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────

export default function FieldConfigPanel({ col, anchorEl, onClose, onFieldChange }) {
  const open = Boolean(anchorEl) && Boolean(col);

  // ── Width — local draft, syncs when col changes ──────────────────────
  const [widthDraft, setWidthDraft] = useState(col?.width ?? 150);
  useEffect(() => {
    if (col) setWidthDraft(col.width ?? 150);
  }, [col?.field, col?.width]);

  // ── Header tooltip — local draft, saves on blur / close ─────────────
  const [tipDraft, setTipDraft] = useState(col?.headerTooltip ?? "");
  useEffect(() => {
    if (col) setTipDraft(col.headerTooltip ?? "");
  }, [col?.field, col?.headerTooltip]);

  const commitTip = useCallback(() => {
    if (!col) return;
    const next = tipDraft.trim();
    // Only fire if changed
    if (next !== (col.headerTooltip ?? "")) {
      onFieldChange(col.field, "headerTooltip", next || undefined);
    }
  }, [col, tipDraft, onFieldChange]);

  // Commit tooltip on panel close too
  const handleClose = useCallback(() => {
    commitTip();
    onClose();
  }, [commitTip, onClose]);

  if (!col) return null;

  const sliderMax  = Math.max((col.width ?? 150) * 2, 400);
  const currentPin = col.pinned;                    // "left" | "right" | undefined
  const sort       = col.sort || {};
  const hasSortSet = !!(sort.direction);
  const isMetaPanel = !!col.metaPanel;

  // Custom props — anything that isn't a standard structural field
  const customEntries = [
    col.fieldType            && { key: "fieldType",              label: "Field type",     value: col.fieldType },
    col.dataSource           && { key: "dataSource",             label: "Data source",    value: col.dataSource },
    col.renderType           && { key: "renderType",             label: "Render type",    value: col.renderType },
    col.type                 && { key: "type",                   label: "Type",           value: col.type },
    col.optionsSource        && { key: "optionsSource",          label: "Options source", value: col.optionsSource },
    col.options?.length      && { key: "options",                label: `Options`,        value: `${col.options.length} items: ${col.options.map(o => o.name).join(", ")}` },
    col.attributes           && { key: "attributes",             label: "Attributes",     value: JSON.stringify(col.attributes) },
    col.details              && { key: "details",                label: "Details (AI)",   value: JSON.stringify(col.details) },
    col.affiliationsDataSource && { key: "affiliations",         label: "Affiliations",   value: col.affiliationsDataSource },
  ].filter(Boolean);

  const hasCustom = customEntries.length > 0;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ className: "fop-paper" }}
      disableRestoreFocus
      disableScrollLock
    >
      {/* ── Panel header ─────────────────────────────────────── */}
      <div className="fop-header">
        <span className="fop-header-field">{col.headerName || col.field}</span>
        <span className="fop-header-type">{col.fieldType || col.type || "field"}</span>
      </div>

      {/* ══════════════════════════════════════════════════════
          WIDTH
          ══════════════════════════════════════════════════════ */}
      <Section icon={<WidthNormalIcon style={{ fontSize: 13 }} />} label="Width">
        <div className="fop-width-row">
          <Slider
            size="small"
            min={0}
            max={sliderMax}
            value={widthDraft}
            onChange={(_, v) => {
              setWidthDraft(v);
              onFieldChange(col.field, "width", v);
            }}
            aria-label="column width"
            sx={{
              flex: 1,
              color: "#4fc3f7",
              "& .MuiSlider-thumb": {
                width: 12,
                height: 12,
                "&:hover, &.Mui-focusVisible": {
                  boxShadow: "0 0 0 6px rgba(79,195,247,0.18)",
                },
              },
              "& .MuiSlider-rail": { opacity: 0.25 },
            }}
          />
          <span className="fop-width-value">{widthDraft}px</span>
        </div>
      </Section>

      <Divider className="fop-divider" />

      {/* ══════════════════════════════════════════════════════
          PIN
          ══════════════════════════════════════════════════════ */}
      <Section
        icon={currentPin
          ? <PushPinIcon         style={{ fontSize: 13 }} />
          : <PushPinOutlinedIcon style={{ fontSize: 13 }} />
        }
        label="Pin column"
      >
        <div className="fop-pill-row">
          <Pill
            label="Left"
            active={currentPin === "left"}
            onClick={() => onFieldChange(col.field, "pinned",
              currentPin === "left" ? undefined : "left"
            )}
          />
          <Pill
            label="Right"
            active={currentPin === "right"}
            onClick={() => onFieldChange(col.field, "pinned",
              currentPin === "right" ? undefined : "right"
            )}
          />
          {currentPin && (
            <Pill
              label="Unpin"
              danger
              onClick={() => onFieldChange(col.field, "pinned", undefined)}
            />
          )}
        </div>
      </Section>

      <Divider className="fop-divider" />

      {/* ══════════════════════════════════════════════════════
          SORT
          ══════════════════════════════════════════════════════ */}
      <Section
        icon={<SwapVertIcon style={{ fontSize: 13 }} />}
        label="Sort"
      >
        {/* Direction */}
        <div className="fop-pill-row">
          {SORT_DIRECTIONS.map(({ value, label }) => (
            <Pill
              key={value}
              label={label}
              active={sort.direction === value}
              onClick={() => {
                if (sort.direction === value) {
                  // Clicking active direction clears sort entirely
                  onFieldChange(col.field, "sort", undefined);
                } else {
                  onFieldChange(col.field, "sort", {
                    direction: value,
                    order: sort.order ?? 1,
                  });
                }
              }}
            />
          ))}
        </div>

        {/* Order — only shown when a direction is set */}
        {hasSortSet && (
          <div className="fop-sort-order-row">
            <span className="fop-sort-order-label">Priority</span>
            <div className="fop-pill-row fop-pill-row--tight">
              {SORT_ORDERS.map((n) => (
                <Pill
                  key={n}
                  label={String(n)}
                  active={(sort.order ?? 1) === n}
                  onClick={() =>
                    onFieldChange(col.field, "sort", { ...sort, order: n })
                  }
                />
              ))}
            </div>
          </div>
        )}
      </Section>

      <Divider className="fop-divider" />

      {/* ══════════════════════════════════════════════════════
          META PANEL
          ══════════════════════════════════════════════════════ */}
      <Section
        icon={<ViewSidebarOutlinedIcon style={{ fontSize: 13 }} />}
        label="Meta panel"
      >
        <div className="fop-pill-row">
          <Pill
            label="Show"
            active={isMetaPanel}
            onClick={() => onFieldChange(col.field, "metaPanel", true)}
          />
          <Pill
            label="Hide"
            active={!isMetaPanel}
            onClick={() => onFieldChange(col.field, "metaPanel", false)}
          />
        </div>
      </Section>

      <Divider className="fop-divider" />

      {/* ══════════════════════════════════════════════════════
          HEADER TOOLTIP
          ══════════════════════════════════════════════════════ */}
      <Section
        icon={<ChatBubbleOutlineIcon style={{ fontSize: 13 }} />}
        label="Header tooltip"
      >
        <TextField
          multiline
          minRows={2}
          maxRows={5}
          size="small"
          fullWidth
          value={tipDraft}
          onChange={(e) => setTipDraft(e.target.value)}
          onBlur={commitTip}
          placeholder="Tooltip shown on column header…"
          className="fop-tooltip-input"
          inputProps={{ style: { fontSize: 11.5, lineHeight: 1.5 } }}
        />
      </Section>

      {/* ══════════════════════════════════════════════════════
          FIELD CONFIG (read-only, custom/schema props)
          ══════════════════════════════════════════════════════ */}
      {hasCustom && (
        <>
          <Divider className="fop-divider" />
          <Section
            icon={<TuneIcon style={{ fontSize: 13 }} />}
            label="Field config"
          >
            <div className="fop-kv-grid">
              {customEntries.map(({ key, label, value }) => (
                <React.Fragment key={key}>
                  <span className="fop-kv-key">{label}</span>
                  <span className="fop-kv-val">{value}</span>
                </React.Fragment>
              ))}
            </div>
            <p className="fop-kv-hint">Use "Edit JSON" to modify these values</p>
          </Section>
        </>
      )}
    </Popover>
  );
}